// Единая точка работы с прокси для сгенерированных файлов (KIE CDN и пр.).
//
// Зачем нужно:
// - KIE CDN у части пользователей (особенно РФ) медленный/заблокирован, прямой
//   запрос из браузера висит или падает по CORS.
// - Атрибут <a download> игнорируется браузером для cross-origin ссылок, поэтому
//   прямое скачивание по KIE-ссылке открывает вкладку вместо сохранения файла.
// - KIE-ссылку нельзя показывать пользователю: в браузере должен фигурировать
//   только URL под нашим доменом.
//
// Решение: всё проксируем через /api/proxy-image (наш домен), а скачиваем через
// blob, чтобы download работал независимо от origin.

const PROXY_PATH = '/api/proxy-image';

// Превращает ссылку в URL под нашим доменом.
// blob:/data:/относительные/уже-наши URL возвращаются без изменений (идемпотентно).
export function getProxiedUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }
  const u = url.trim();
  if (!u) {
    return url;
  }
  // blob: и data: — это уже локальные данные, проксировать нечего
  if (u.startsWith('blob:') || u.startsWith('data:')) {
    return u;
  }
  // Относительные пути и уже-наши прокси-URL (в т.ч. ?token=...) оставляем как есть
  if (u.startsWith('/')) {
    return u;
  }
  // Абсолютные http(s)
  if (/^https?:\/\//i.test(u)) {
    try {
      const origin = (typeof window !== 'undefined' && window.location)
        ? window.location.origin
        : 'https://airun.art';
      const parsed = new URL(u, origin);
      // Уже проксированный URL — не оборачиваем повторно
      if (parsed.pathname.startsWith(PROXY_PATH)) {
        return u;
      }
      // Наш собственный домен — проксировать не нужно
      if (typeof window !== 'undefined' && window.location && parsed.origin === window.location.origin) {
        return u;
      }
    } catch (e) {
      // невалидный URL — отдаём как есть
      return u;
    }
    return `${PROXY_PATH}?url=${encodeURIComponent(u)}`;
  }
  return u;
}

function triggerBlobDownload(href, filename, revoke) {
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', filename || 'download');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  if (revoke) {
    // Даём браузеру время начать загрузку перед освобождением памяти
    setTimeout(() => {
      try { URL.revokeObjectURL(href); } catch (e) {}
    }, 10000);
  }
}

// Скачивает файл через прокси (быстрый ?url-режим, без токена) и возвращает Blob.
// Нужно, чтобы свежий результат показывался/скачивался мгновенно из blob, не дожидаясь
// медленной пропагации токена в n8n. Возвращает null при неудаче.
export async function fetchProxiedBlob(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }
  // 1) Через прокси под нашим доменом (обходит CORS/блокировки CDN)
  try {
    const res = await fetch(getProxiedUrl(url));
    if (res.ok) {
      const b = await res.blob();
      if (b && b.size > 0) return b;
    }
  } catch (e) {}
  // 2) Fallback: прямой CORS-fetch
  try {
    const res = await fetch(url);
    if (res.ok) {
      const b = await res.blob();
      if (b && b.size > 0) return b;
    }
  } catch (e) {}
  return null;
}

// Возвращает blob: object URL для картинки (или null). URL под нашим доменом, KIE-ссылка скрыта.
export async function fetchProxiedBlobUrl(url) {
  const blob = await fetchProxiedBlob(url);
  return blob ? URL.createObjectURL(blob) : null;
}

// Надёжное скачивание файла независимо от origin.
// lockRef — useRef(false) для защиты от двойного клика (опционально).
export async function downloadFile(url, filename, lockRef) {
  if (!url) {
    return;
  }
  if (lockRef && lockRef.current) {
    return;
  }
  if (lockRef) {
    lockRef.current = true;
  }
  try {
    // Локальные данные качаем напрямую — download-атрибут тут работает
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      triggerBlobDownload(url, filename, false);
      return;
    }

    let blob = null;

    // 1) Через прокси (под нашим доменом) — обходит CORS/блокировки CDN
    try {
      const res = await fetch(getProxiedUrl(url));
      if (res.ok) {
        blob = await res.blob();
      }
    } catch (e) {
      // прокси недоступен — пробуем прямой fetch ниже
    }

    // 2) Fallback: прямой CORS-fetch
    if (!blob) {
      try {
        const res = await fetch(url);
        if (res.ok) {
          blob = await res.blob();
        }
      } catch (e) {
        // оба способа упали — откроем в новой вкладке ниже
      }
    }

    if (blob && blob.size > 0) {
      const objUrl = URL.createObjectURL(blob);
      triggerBlobDownload(objUrl, filename, true);
      return;
    }

    // 3) Последний fallback — открыть в новой вкладке (под нашим доменом)
    window.open(getProxiedUrl(url), '_blank', 'noopener,noreferrer');
  } finally {
    if (lockRef) {
      lockRef.current = false;
    }
  }
}
